import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { feesService } from '../../services/feesService';
import { useToast } from '../../contexts/ToastContext';
import { useSelector } from 'react-redux';
import PaymentTypeModal from './PaymentTypeModal';

const FeeDetailModal = ({ isOpen, onClose, student, onUpdate }) => {
  const { success, error } = useToast();
  const { user } = useSelector((state) => state.auth);
  const [feeRecords, setFeeRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingFeeId, setUpdatingFeeId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [pendingFeeUpdate, setPendingFeeUpdate] = useState(null);

  useEffect(() => {
    if (isOpen && student) {
      setFeeRecords(student.feeRecords || []);
    }
  }, [isOpen, student]);

  const handleUpdateFeeStatusClick = (feeId, feeInfo, newStatus) => {
    if (newStatus === 'paid') {
      // Show payment type modal first
      setPendingFeeUpdate({ feeId, feeInfo, newStatus });
      setPaymentModalOpen(true);
    } else {
      // Directly update for pending/overdue
      handleUpdateFeeStatus(feeId, newStatus, null, null);
    }
  };

  const handlePaymentConfirm = (paymentData) => {
    if (pendingFeeUpdate) {
      handleUpdateFeeStatus(
        pendingFeeUpdate.feeId,
        pendingFeeUpdate.newStatus,
        paymentData.paymentType,
        paymentData.transactionId
      );
      setPaymentModalOpen(false);
      setPendingFeeUpdate(null);
    }
  };

  const handleUpdateFeeStatus = async (feeId, newStatus, paymentType = null, transactionId = null) => {
    setUpdatingFeeId(feeId);
    try {
      const updateData = {
        status: newStatus,
        paymentDate: newStatus === 'paid' ? Date.now() : null,
        paymentMethod: newStatus === 'paid' ? (paymentType || 'cash') : null,
        transactionId: newStatus === 'paid' && transactionId ? transactionId : null
      };

      const response = await feesService.updateFeeStatus(feeId, updateData, user.token);
      if (response.success) {
        success('Fee status updated successfully');
        
        // Update local state
        setFeeRecords(prevRecords =>
          prevRecords.map(fee =>
            fee._id === feeId
              ? { 
                  ...fee, 
                  status: newStatus, 
                  paymentDate: updateData.paymentDate,
                  paymentMethod: updateData.paymentMethod,
                  transactionId: updateData.transactionId
                }
              : fee
          )
        );
        
        // Notify parent to refresh
        if (onUpdate) {
          onUpdate();
        }
      } else {
        error('Failed to update fee status');
      }
    } catch (err) {
      console.error('Error updating fee status:', err);
      error('Failed to update fee status');
    } finally {
      setUpdatingFeeId(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'overdue':
        return <AlertTriangle size={16} className="text-red-600" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-600" />;
      default:
        return <Clock size={16} className="text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (typeof timestamp === 'number') {
      return new Date(timestamp).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
    return new Date(timestamp).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!isOpen || !student) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Fee Details</h2>
              <p className="text-sm text-gray-600 mt-1">{student.name} - {student.email}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Fee Summary */}
          {student.feeSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700 font-medium">Total Fees</p>
                <p className="text-2xl font-bold text-blue-900">
                  ₹{(student.feeSummary.totalFees || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 font-medium">Paid</p>
                <p className="text-2xl font-bold text-green-900">
                  ₹{(student.feeSummary.paidFees || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-700 font-medium">Pending</p>
                <p className="text-2xl font-bold text-yellow-900">
                  ₹{(student.feeSummary.pendingFees || 0).toLocaleString()}
                </p>
              </div>
              {student.feeSummary.overdueFees > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700 font-medium">Overdue</p>
                  <p className="text-2xl font-bold text-red-900">
                    ₹{(student.feeSummary.overdueFees || 0).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Fee Records Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Term
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Academic Year
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {feeRecords.length > 0 ? (
                  feeRecords.map((fee) => (
                    <tr key={fee._id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          Term {fee.termNumber}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{fee.academicYear}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          ₹{(fee.amount || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {formatDate(fee.dueDate)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(fee.status)}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(fee.status)}`}>
                            {fee.status.charAt(0).toUpperCase() + fee.status.slice(1)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {fee.paymentDate ? (
                          <div>
                            <span className="text-sm text-gray-600">{formatDate(fee.paymentDate)}</span>
                            {fee.paymentMethod && (
                              <p className="text-xs text-gray-500 capitalize">{fee.paymentMethod}</p>
                            )}
                            {fee.transactionId && (
                              <p className="text-xs text-blue-600">TXN: {fee.transactionId}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-600">Not paid</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          {fee.status !== 'paid' && (
                            <button
                              onClick={() => handleUpdateFeeStatusClick(fee._id, fee, 'paid')}
                              disabled={updatingFeeId === fee._id}
                              className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updatingFeeId === fee._id ? 'Updating...' : 'Mark Paid'}
                            </button>
                          )}
                          {fee.status === 'paid' && (
                            <button
                              onClick={() => handleUpdateFeeStatus(fee._id, 'pending', null, null)}
                              disabled={updatingFeeId === fee._id}
                              className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updatingFeeId === fee._id ? 'Updating...' : 'Mark Pending'}
                            </button>
                          )}
                          {fee.status !== 'overdue' && (
                            <button
                              onClick={() => handleUpdateFeeStatus(fee._id, 'overdue', null, null)}
                              disabled={updatingFeeId === fee._id}
                              className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updatingFeeId === fee._id ? 'Updating...' : 'Mark Overdue'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No fee records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      <PaymentTypeModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPendingFeeUpdate(null);
        }}
        onConfirm={handlePaymentConfirm}
        feeInfo={pendingFeeUpdate?.feeInfo}
      />
    </div>
  );
};

export default FeeDetailModal;

